import { CreditCategory, TaxDocStatus, TaxpayerCategory } from '../../../interfaces/corporateTax.interface';
import { humanizeEnum } from '../../../utils/format';

export const CATEGORY_LABEL: Record<CreditCategory, string> = {
    IMPORT_AIT: 'Import AIT',
    EXPORT_SOURCE_TAX: 'Export source tax',
    CASH_INCENTIVE_AIT: 'Cash incentive AIT',
    CUSTOMER_TDS: 'Customer TDS',
    BANK_INTEREST_AIT: 'Bank interest AIT',
    ADVANCE_TAX_PAID: 'Advance tax paid',
    SET_OFF: 'Set-off',
    OTHER: 'Other / unmapped',
};

export const TAXPAYER_LABEL: Record<TaxpayerCategory, string> = {
    LISTED_COMPANY: 'Listed company',
    NON_LISTED_COMPANY: 'Non-listed company',
    SMALL_COMPANY: 'Small company',
    BANK_FINANCIAL_INSTITUTION: 'Bank / financial institution',
    RMG_EXPORTER: 'Garment (RMG) exporter',
    OTHER: 'Other',
};

export const DOC_STATUS_COLOR: Record<TaxDocStatus, 'default' | 'success' | 'warning'> = {
    DRAFT: 'warning', POSTED: 'success', REVERSED: 'default',
};

export const statusLabel = (s?: string | null) => humanizeEnum(s);

const pad = (n: number) => String(n).padStart(2, '0');

/** First day of the fiscal year that starts in calendar year {@code incomeYear}, built without toISOString. */
export const fiscalYearStartIso = (incomeYear: number, startMonth: number): string =>
    `${incomeYear}-${pad(startMonth)}-01`;

/** The income year (calendar year its fiscal year starts in) containing {@code now}. */
export const currentIncomeYear = (startMonth: number, now: Date = new Date()): number =>
    now.getMonth() + 1 >= startMonth ? now.getFullYear() : now.getFullYear() - 1;

/**
 * The income year a NEW computation starts on: the latest income year that has rates and is not in the future
 * (the current year first, since an income year can be computed as it runs), else the previous year, so the
 * form never opens on a year the preview would refuse for want of rates.
 */
export const defaultComputationYear = (rateYears: number[], startMonth: number, now: Date = new Date()): number => {
    const cur = currentIncomeYear(startMonth, now);
    const usable = rateYears.filter(y => y <= cur);
    return usable.length > 0 ? Math.max(...usable) : cur - 1;
};

/** Label such as "FY 2025-26" or "FY 2025" for a calendar-year fiscal year. */
export const incomeYearLabel = (incomeYear: number, startMonth: number): string =>
    startMonth === 1 ? `FY ${incomeYear}` : `FY ${incomeYear}-${String((incomeYear + 1) % 100).padStart(2, '0')}`;

/** The last six income years, newest first, for a year picker. */
export const incomeYearOptions = (startMonth: number, now: Date = new Date()): number[] => {
    const cur = currentIncomeYear(startMonth, now);
    return [0, 1, 2, 3, 4, 5].map(i => cur - i);
};

export const journalHref = (id?: string | null) => (id ? `/accounting/journal-entries/${id}` : undefined);

/** Where the document behind a ledger line lives, by reference type. */
export const documentHref = (referenceType?: string | null, referenceId?: string | null, entryId?: string | null): string | undefined => {
    if (referenceType === 'BILL_OF_ENTRY' && referenceId) return `/procurement/bills-of-entry/${referenceId}`;
    return journalHref(entryId);
};
